import {Form, Input, Select, Upload, Button} from 'antd';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {toast} from 'react-toastify';
import {isDev} from 'utils/constants';
import {useState} from 'react';
import styles from './walletTransaction.module.scss';
import cloudIcon from 'images/cloud-upload.svg';
import {walletCurrencies} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {BULK_UPDATE_PENDING_TRANSACTIONS} from 'graphql/mutations/wallet';
import {useMutation} from '@apollo/client';

export const BulkUploadWithdrawals = ({form, onClose, refetch}) => {
  const csvUrl = Form.useWatch('csv', form);
  const [loadingCSVUpload, setLoadingCSVUpload] = useState(false);

  const [
    bulkUpdatePendingTransactions,
    {loading: bulkUpdatePendingTransactionsLoading},
  ] = useMutation(BULK_UPDATE_PENDING_TRANSACTIONS, {
    onError: error => toast.error(error.message),
    errorPolicy: 'all',
  });

  // v6 of the sample sheet:
  //   col 11 (par[10]) transferId              — duplicate-guard
  //   col 12 (par[11]) newTotalAmount          — amount override (paired)
  //   col 13 (par[12]) amountAdjustmentReason  — amount override (paired)
  //   col 14 (par[13]) failureReasonCode       — server-side registry code
  //                                              or 'OTHER'
  //   col 15 (par[14]) failureReasonMessageEN  — optional EN override
  //   col 16 (par[15]) failureReasonMessageFR  — optional FR override
  //   col 17 (par[16]) codeLabel               — PHANTOM. Parser doesn't
  //                                              read it. Kept in the
  //                                              sample as an inline
  //                                              cheatsheet so admins can
  //                                              see every code and its
  //                                              human-readable label
  //                                              side-by-side without
  //                                              opening a second file.
  //
  // The sample carries one reference row per failure code (32 rows: 16
  // DEP_* + 16 WTH_* + 1 OTHER). Those rows use fake transactionIds like
  // `depX000…dep1` that fail an ObjectId regex, so the parser drops them
  // before they reach the resolver — leaving them in the sheet is safe.
  const handleSampleCSV = () => {
    window.open(
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/withdrawals/daba-finance-bulk-update-pending-txs-sample-with-code-cheatsheet-v6-2026-09-22.csv',
      '_blank',
    );
  };

  const beforeUpload = file => {
    // Prevent upload if already uploading
    if (loadingCSVUpload) {
      toast.warning('Upload already in progress');
      return false;
    }

    // Check if file is CSV
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    if (!isCSV) {
      toast.error('You can only upload CSV files!');
      return false;
    }

    // Check file size (max 10MB)
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      toast.error('File must be smaller than 10MB!');
      return false;
    }

    return true;
  };

  const handleCSVUpload = async info => {
    const {file} = info;

    // Only process if file status is 'uploading' and we haven't processed this file yet
    if (file.status === 'uploading' && !loadingCSVUpload) {
      try {
        setLoadingCSVUpload(true);

        const bucket = isDev
          ? 'dabafinancialinstruments-nonprod'
          : 'dabafinancialinstruments-prod';

        const fileLink = await UploadDocumentToBucket(
          file.originFileObj,
          () => {},
          bucket,
          true,
          'withdrawals',
        );
        console.log(fileLink);
        form.setFieldsValue({csv: fileLink});
        toast.success('CSV file uploaded successfully!');

        // Update file status to done
        file.status = 'done';
      } catch (error) {
        console.error('Error uploading CSV:', error);
        toast.error('Failed to upload CSV file');
        file.status = 'error';
      } finally {
        setLoadingCSVUpload(false);
      }
    }
  };

  const getUploadClassName = () => {
    if (loadingCSVUpload) return styles.uploadLoading;
    if (csvUrl) return styles.uploadSuccess;
    return '';
  };

  const handleFinish = values => {
    console.log(values);
    bulkUpdatePendingTransactions({
      variables: {
        input: {
          currency: values.currency,
          csv: values.csv,
          userEmail: values?.email,
        },
      },
    })
      .then(({data: {bulkUpdatePendingTransactions}}) => {
        if (
          bulkUpdatePendingTransactions?.statusCode === 400 ||
          bulkUpdatePendingTransactions?.__typename === 'Error'
        ) {
          toast.error(bulkUpdatePendingTransactions?.message);
        } else {
          toast.success('Transactions updated successfully');
          onClose();
          refetch();
        }
      })
      .catch(error => {
        console.log(error);
      });
  };

  return (
    <div className={styles.bulkUploadContainer}>
      <h1>Bulk Update Pending Transactions</h1>
      <p>
        Upload a CSV following the sample format.
      </p>
      <ul style={{marginTop: -8, marginBottom: 12, paddingLeft: 20, lineHeight: 1.5}}>
        <li>
          <strong>Column 11 (transferId)</strong> — required for any WISE /
          REVOLUT / WIRE / BANK / DWOLLA / INTERAC row being approved as
          SUCCESSFUL. Missing values are rejected loudly (one wire must never
          move twice).
        </li>
        <li>
          <strong>Columns 12–13 (newTotalAmount, amountAdjustmentReason)</strong> —
          optional. Fill both together to settle a PENDING deposit at a
          different amount than the user submitted (mirrors the "Adjust Amount"
          field on the single-update modal). Leave blank on rows that don't
          need an override; either without the other is rejected.
        </li>
        <li>
          <strong>Columns 14–16 (failureReasonCode, failureReasonMessageEN,
          failureReasonMessageFR)</strong> — optional, only stored on FAILED
          rows. On a known code (e.g. <code>DEP_003</code>, <code>WTH_012</code>)
          the server looks up the EN / FR text automatically — you can leave
          cols 15–16 blank. Use <code>OTHER</code> + cols 15–16 for freeform
          text. Cols 15–16 also act as a per-row override on a known code if
          you want to reword its default text.
        </li>
        <li>
          <strong>Column 17 (codeLabel)</strong> — <em>phantom</em>. The parser
          doesn't read it. It's in the sample so you can see every failure
          code with its human-readable label side-by-side. The sample has one
          reference row per code (fake transactionIds, silently dropped by
          the parser). Leave the column in your real sheet or delete it —
          either way it has no effect on the update.
        </li>
      </ul>
      <div className={styles.sampleCsvSection}>
        <Button
          type="link"
          onClick={handleSampleCSV}
          className={styles.sampleCsvButton}>
          📄 View Sample CSV Format
        </Button>
      </div>
      <Form
        onFinish={handleFinish}
        form={form}
        layout="vertical"
        className={styles.bulkUploadForm}>
        <Form.Item
          label="Upload CSV File"
          name="csv"
          className={`${styles.csvField} ${getUploadClassName()}`}
          rules={[{required: true, message: 'Please upload a CSV file'}]}>
          <Upload
            accept=".csv"
            maxCount={1}
            beforeUpload={beforeUpload}
            onChange={handleCSVUpload}
            disabled={loadingCSVUpload}
            showUploadList={false}
            listType="text"
            customRequest={({onSuccess}) => {
              // Custom request to prevent automatic upload
              setTimeout(() => {
                onSuccess('ok');
              }, 0);
            }}>
            <div className={styles.csvUpload}>
              <img src={cloudIcon} alt="Upload" />
              <span>
                {loadingCSVUpload
                  ? 'Uploading...'
                  : csvUrl
                  ? 'CSV Uploaded Successfully'
                  : 'Drag and drop your CSV file here'}
              </span>
              <span className={styles.uploadText}>
                or click to browse files
              </span>
            </div>
          </Upload>
        </Form.Item>
        <Form.Item
          name="currency"
          rules={[{required: true, message: 'Please select a currency'}]}
          label="Currency">
          <Select
            options={walletCurrencies?.map(currency => ({
              label: currency.key,
              value: currency.key,
            }))}
            placeholder="Select Currency"
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          rules={[{type: 'email', message: 'Please enter a valid email'}]}
          name="email"
          label="Email">
          <Input placeholder="Enter Email" />
        </Form.Item>
        <SubmitButton
          disabled={bulkUpdatePendingTransactionsLoading}
          label="Submit"
        />
      </Form>
    </div>
  );
};
